// =======================================================
// ScanlineFlood — dense moving scanlines with rhythmic flashes
// =======================================================
//
// Pure fullscreen shader, very cheap. Perfect for ultrawide strips —
// horizontal lines read beautifully on a 20:1 ribbon.
//
// Technique: divide the screen into N rows, each row scrolls at its own
// rate with a random phase. A beat signal flashes random rows.

import * as THREE from 'three';
import { Generator, GLSL_COMMON } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform float uLoopPhase;
  uniform float uBeatPhase;
  uniform float uBeatIndex;
  uniform float uBeats;
  uniform vec2  uResolution;
  uniform vec3  uColor;
  uniform vec3  uBg;
  uniform float uDensity;      // number of rows
  uniform float uSpeed;        // line scroll speed multiplier
  uniform float uThickness;    // px thickness
  uniform float uVariance;     // speed variance per row
  uniform float uFlash;        // beat-flash strength 0..1
  uniform float uBpm;
  uniform float uDirection;    // 0 = horizontal bars, 1 = vertical bars

  ${GLSL_COMMON}

  void main() {
    vec2 uv = vUv;
    vec2 frag = uv * uResolution;

    // orientation: if vertical, swap so "row" logic runs on x
    vec2 p = mix(uv, uv.yx, uDirection);
    vec2 res = mix(uResolution, uResolution.yx, uDirection);

    float rows = uDensity;
    float rowF = p.y * rows;
    float rowIdx = floor(rowF);

    // deterministic per-row speed and phase
    float rSpeed = (hash11(rowIdx * 0.173 + 1.3) - 0.5) * 2.0 * uVariance + 1.0;
    float rPhase = hash11(rowIdx * 0.379 + 7.7);

    // where along the row, in [0,1]
    float cycles = max(1.0, floor(uSpeed / 80.0 + 0.5));
    float t = fract(p.x - uLoopPhase * cycles * rSpeed + rPhase);

    // thin line: a bright core near the center of its span
    // we treat every row's pixel as "on" in a thin band whose width is
    // controlled by uThickness (in pixels, measured on the row's axis).
    float pxPerRow = res.y / rows;
    float thNorm = uThickness / pxPerRow;                  // thickness in [0,1] of a row cell
    float yInCell = fract(rowF);                            // 0..1 within the row
    float band = smoothstep(thNorm, 0.0, abs(yInCell - 0.5));

    // brightness falls off along the length, with some variation
    float lenFade = smoothstep(0.0, 0.15, t) * smoothstep(1.0, 0.85, t);
    float base = band * lenFade * 0.6;

    // beat flash
    float pulse = pow(1.0 - uBeatPhase, 4.0);
    float rowLit = step(0.75, hash11(rowIdx * 0.91 + mod(uBeatIndex, uBeats)));
    float flash = rowLit * pulse * uFlash * band;

    float brightness = base + flash;
    vec3 col = mix(uBg, uColor, clamp(brightness * 3.0, 0.0, 1.5));

    gl_FragColor = vec4(col, 1.0);
  }
`;

export class ScanlineFlood extends Generator {
  constructor() {
    super({
      name: 'Scanline Flood',
      category: 'intense',
      schema: {
        density:   { default: 60,  min: 4,    max: 300, step: 1,    label: 'Line count' },
        speed:     { default: 55,  min: 0,    max: 800, step: 1,    label: 'Speed' },
        thickness: { default: 1.2, min: 0.5,  max: 20,  step: 0.1,  label: 'Thickness (px)' },
        variance:  { default: 0.8, min: 0,    max: 2,   step: 0.01, label: 'Speed variance' },
        flash:     { default: 0.6, min: 0,    max: 1.5, step: 0.01, label: 'Flash strength' },
        bpm:       { default: 120, min: 30,   max: 240, step: 1,    label: 'BPM' },
        direction: { default: 'h', type: 'select', label: 'Direction',
                     options: [['h','Horizontal bars'],['v','Vertical bars']] },
        accent:    { default: '#62ffd8', type: 'color', label: 'Color' },
      }
    });
  }

  onEnter(scene, engine) {
    this.engine = engine;
    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: FRAG,
      uniforms: {
        uLoopPhase:  { value: 0 },
        uBeatPhase:  { value: 0 },
        uBeatIndex:  { value: 0 },
        uBeats:      { value: engine.timeline.beats },
        uResolution: { value: new THREE.Vector2(engine.width, engine.height) },
        uColor:      { value: new THREE.Color(this.v('accent')) },
        uBg:         { value: new THREE.Color('#000000') },
        uDensity:    { value: this.v('density') },
        uSpeed:      { value: this.v('speed') },
        uThickness:  { value: this.v('thickness') },
        uVariance:   { value: this.v('variance') },
        uFlash:      { value: this.v('flash') },
        uBpm:        { value: this.v('bpm') },
        uDirection:  { value: this.v('direction') === 'v' ? 1 : 0 },
      },
      transparent: false,
      depthWrite: false,
      depthTest: false
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
  }

  onResize(w, h) {
    if (this.mesh) this.mesh.material.uniforms.uResolution.value.set(w, h);
  }

  onParamChange(key, val) {
    if (!this.mesh) return;
    const u = this.mesh.material.uniforms;
    if (key === 'accent')    u.uColor.value.set(val);
    if (key === 'density')   u.uDensity.value = val;
    if (key === 'speed')     u.uSpeed.value = val;
    if (key === 'thickness') u.uThickness.value = val;
    if (key === 'variance')  u.uVariance.value = val;
    if (key === 'flash')     u.uFlash.value = val;
    if (key === 'bpm')       u.uBpm.value = val;
    if (key === 'direction') u.uDirection.value = val === 'v' ? 1 : 0;
  }

  update(dt, time, engine) {
    if (!this.mesh) return;
    const loop = engine.getLoopInfo(time);
    const u = this.mesh.material.uniforms;
    u.uLoopPhase.value = loop.phase;
    u.uBeatPhase.value = loop.beatPhase;
    u.uBeatIndex.value = loop.beatIndex;
    u.uBeats.value = loop.beats;
  }
}
